import type { ReactNode } from 'react';
import './Gauge.css';

/**
 * Gauge — **반원 게이지** (2026-09-15 · 대시보드 시안 v2 V0 · 정본 제안 D11-4 `#GAUGE`).
 *
 * 쓰는 곳: 이번 주 요약 「회의 비중」(사용자 결정 D-Q4 — 원본 캡처의 게이지 · 누적 막대 · treemap 중 게이지만).
 *
 * ★★★ **모르는 값은 빈 호가 아니다** (`Progress` 와 같은 규칙) — `value={null}` 이면 채우지 않고 「—」로 적는다.
 *     0 을 주면 0% 라고 적는다 — 그것이 사실일 때만 준다.
 * ★★ 스크린리더는 그림을 못 읽는다 — 그림 전체를 `role="img"` 하나로 두고 이름에 값까지 적는다(「회의 비중 32%」).
 * ★ 값은 **0~100 백분율**이다. 계산(회의 분 ÷ 근무 분)은 부르는 쪽이 한다 — 게이지는 규칙을 모른다.
 * ★ 호 길이는 `pathLength=100` 으로 둬 값이 곧 dash 길이다(반지름 · 크기와 무관).
 */

export type GaugeTone = 'primary' | 'success' | 'warning' | 'danger' | 'neutral';

export interface GaugeProps {
  /** 0~100. 모르면 null. 범위 밖은 잘라서 그린다. */
  value: number | null;
  /** 무엇의 비중인가 — 「회의 비중」. */
  label: string;
  /** 값 아래 작은 글자 뒤쪽 — 「지난주 27%」. */
  caption?: ReactNode;
  tone?: GaugeTone;
  className?: string;
}

/** 0~100 으로 자르고 반올림. 모르면 null. 순수 함수라 검사에서 직접 부른다. */
export function gaugePercent(value: number | null): number | null {
  if (value == null || !Number.isFinite(value)) return null;
  return Math.round(Math.min(100, Math.max(0, value)));
}

const ARC = 'M10 60 A50 50 0 0 1 110 60';

export function Gauge({ value, label, caption, tone = 'primary', className }: GaugeProps) {
  const pct = gaugePercent(value);
  const text = pct == null ? '—' : `${pct}%`;
  return (
    <div
      className={['wc-gauge', className ?? ''].filter(Boolean).join(' ')}
      role="img"
      aria-label={`${label} ${pct == null ? '모름' : text}`}
    >
      <svg viewBox="0 0 120 66" aria-hidden focusable="false">
        <path className="wc-gauge__track" d={ARC} pathLength={100} />
        {pct != null && (
          <path
            className={`wc-gauge__fill wc-gauge__fill--${tone}`}
            d={ARC}
            pathLength={100}
            strokeDasharray={`${pct} 100`}
          />
        )}
      </svg>
      <span className="wc-gauge__text" aria-hidden>
        <b className="wc-gauge__value">{text}</b>
        <small className="wc-gauge__caption">{label}{caption != null && <> · {caption}</>}</small>
      </span>
    </div>
  );
}
